import React, { Suspense, useEffect, lazy } from "react";
import { Route, BrowserRouter as Router, Routes } from "react-router-dom";
import LayoutMainWithSideDrawer from "./components/LayoutMainWithSideDrawer";
import ProtectedRoute from "./components/ProtectedRoute";
import About from "./pages/About";
import HelpPage from "./pages/Help";
import HomePage from "./pages/Home";
import NotAuthorized from "./pages/NotAuthorized";
import RegionsPage from "./pages/Regions";
import ResultsPage from "./pages/Results";
import Sites from "./pages/Sites";
import SpeciesPage from "./pages/Species";
import Trials from "./pages/Trials";
// Map
import MapPage from "./pages/Map";
//Theme
import { ThemeProvider } from "@mui/material/styles";
import theme from "./styles/ptn-theme";
import { isTokenExpired } from "./utils/auth/identity";
// Google Analytics
import { usePageTracking } from "./hooks/usePageTracking";
//Admin imports
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const AdminSiteContent = lazy(() => import("./pages/admin/AdminSiteContent"));
const AdminImportData = lazy(() => import("./pages/admin/AdminImportData"));
const AdminTrials = lazy(() => import("./pages/admin/AdminTrials"));
const AdminTrialResults = lazy(() => import("./pages/admin/AdminTrialResults"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminVarieties = lazy(() => import("./pages/admin/AdminVarieties"));
const AdminSites = lazy(() => import("./pages/admin/AdminSites"));
const AdminSoilTypes = lazy(() => import("./pages/admin/AdminSoilTypes"));
const AdminSpecies = lazy(() => import("./pages/admin/AdminSpecies"));
const AdminRegions = lazy(() => import("./pages/admin/AdminRegions"));
const AdminRainfall = lazy(() => import("./pages/admin/AdminRainfall"));
const AdminTrialDocumentPage = lazy(() => import("./components/admin/trials/AdminTrialDocument"));
const AdminSync = lazy(() => import("./pages/admin/AdminSync"));
// Component to handle page tracking inside Router
const AppContent = () => {
    // Track page views on route changes
    usePageTracking();
    return (React.createElement(LayoutMainWithSideDrawer, null,
        React.createElement(Routes, null,
            React.createElement(Route, { path: "/", element: React.createElement(HomePage, null) }),
            React.createElement(Route, { path: "/about", element: React.createElement(About, null) }),
            React.createElement(Route, { path: "/help", element: React.createElement(HelpPage, null) }),
            React.createElement(Route, { path: "/map", element: React.createElement(MapPage, null) }),
            React.createElement(Route, { path: "/trials", element: React.createElement(Trials, null) }),
            React.createElement(Route, { path: "/sites", element: React.createElement(Sites, null) }),
            React.createElement(Route, { path: "/regions", element: React.createElement(RegionsPage, null) }),
            React.createElement(Route, { path: "/species", element: React.createElement(SpeciesPage, null) }),
            React.createElement(Route, { path: "/results/:trialID", element: React.createElement(ResultsPage, null) }),
            React.createElement(Route, { path: "/not-authorized", element: React.createElement(NotAuthorized, null) }),
            React.createElement(Route, { path: "/admin/dashboard", element: adminComponent(AdminDashboard) }),
            React.createElement(Route, { path: "/admin/users", element: adminComponent(AdminUsers) }),
            React.createElement(Route, { path: "/admin/sites", element: adminComponent(AdminSites) }),
            React.createElement(Route, { path: "/admin/trials", element: adminComponent(AdminTrials) }),
            React.createElement(Route, { path: "/admin/trial-results", element: adminComponent(AdminTrialResults) }),
            React.createElement(Route, { path: "/admin/varieties", element: adminComponent(AdminVarieties) }),
            React.createElement(Route, { path: "/admin/species", element: adminComponent(AdminSpecies) }),
            React.createElement(Route, { path: "/admin/regions", element: adminComponent(AdminRegions) }),
            React.createElement(Route, { path: "/admin/soil-types", element: adminComponent(AdminSoilTypes) }),
            React.createElement(Route, { path: "/admin/rainfall", element: adminComponent(AdminRainfall) }),
            React.createElement(Route, { path: "/admin/import-data", element: adminComponent(AdminImportData) }),
            React.createElement(Route, { path: "/admin/site-content", element: adminComponent(AdminSiteContent) }),
            React.createElement(Route, { path: "/admin/trial-documents", element: adminComponent(AdminTrialDocumentPage) }),
            React.createElement(Route, { path: "/admin/sync", element: adminComponent(AdminSync) }))));
};
const App = () => {
    useEffect(() => {
        const checkTokenExpiry = () => {
            const token = localStorage.getItem("token");
            if (!token) {
                return;
            }
            const isExpired = isTokenExpired(token !== null && token !== void 0 ? token : "");
            if (isExpired) {
                // remove token from local storage
                console.log("Token expired");
                localStorage.removeItem("token");
                // navigate to home page and alert user that token has expired
                window.location.href = "/";
                alert("Your session has expired, please login again");
            }
        };
        // Check token expiry every 10 seconds
        const intervalId = setInterval(checkTokenExpiry, 10000);
        // Cleanup interval on component unmount
        return () => clearInterval(intervalId);
    }, []);
    return (React.createElement(ThemeProvider, { theme: theme },
        React.createElement(Router, null,
            React.createElement(AppContent, null))));
};
function adminComponent(Component) {
    return React.createElement(Suspense, { fallback: React.createElement("div", null, "Loading admin tools...") },
        React.createElement(ProtectedRoute, { component: Component, roles: ["Site Administrator"] }));
}
export default App;
